import type {
  ThreadMessage,
  ToolCallMessagePartProps,
} from '@assistant-ui/react'
import type { UIInteraction } from '@web/api/types'
import { Markdown } from '@web/components/Markdown'
import { MessageAvatar, useMessageActor } from '@web/components/MessageAvatar'
import {
  MessageInteraction,
  type MessageOrigin,
  originLabel,
  systemOrigin,
} from '@web/components/MessageInteraction'
import { ReasoningBlock } from '@web/components/ReasoningBlock'
import { FallbackTool, TOOL_UIS } from '@web/components/tools'
import { readMessageActor } from 'cc-render'
import { createElement } from 'react'

const noop = () => undefined

/**
 * Renders a subagent's transcript inside its parent tool call. This runs
 * outside assistant-ui's message context, so parts are rendered directly.
 */
export function NestedTranscript({
  messages,
}: {
  messages: readonly ThreadMessage[]
}) {
  return (
    <div className="mt-1 border-l-2 border-line pl-3">
      {messages.map((m) => (
        <div key={m.id} className="my-1.5">
          <NestedActor message={m} />
          {m.role === 'user' && (
            <div className="rounded-control bg-message-accent px-3 py-1.5 text-[12.5px] text-accent-fg text-balance whitespace-pre-wrap">
              <MessageInteraction
                interaction={
                  m.metadata.custom?.interaction as UIInteraction | undefined
                }
                origin={m.metadata.custom?.origin as MessageOrigin | undefined}
                synthetic={m.metadata.custom?.synthetic === true}
                accent
              />
              {m.content.map((p, i) =>
                p.type === 'text' ? <span key={i}>{p.text}</span> : null,
              )}
            </div>
          )}
          {m.role === 'assistant' &&
            m.content.map((p, i) => {
              if (p.type === 'text')
                return (
                  <Markdown key={i} text={p.text} className="text-[12.5px]" />
                )
              if (p.type === 'reasoning')
                return (
                  <ReasoningBlock
                    key={i}
                    text={p.text}
                    running={p.status?.type === 'running'}
                  />
                )
              if (p.type === 'tool-call') {
                const Comp = TOOL_UIS[p.toolName] ?? FallbackTool
                const props = {
                  ...p,
                  status: { type: 'complete' },
                  addResult: noop,
                  resume: noop,
                  respondToApproval: noop,
                } as unknown as ToolCallMessagePartProps
                return createElement(Comp, { key: p.toolCallId, ...props })
              }
              return null
            })}
          {m.role === 'system' && (
            <div className="text-[11px] text-faint">
              {m.content.map((p) => (p.type === 'text' ? p.text : '')).join('')}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

function NestedActor({ message }: { message: ThreadMessage }) {
  const origin = message.metadata.custom?.origin as MessageOrigin | undefined
  const explicit = systemOrigin(origin)
    ? { kind: 'system' as const, id: 'system', name: originLabel(origin) }
    : readMessageActor(message.metadata.custom?.actor)
  const actor = useMessageActor(
    message.role === 'user' ? 'user' : 'assistant',
    explicit,
  )
  if (!explicit) return null
  return (
    <div className="mb-1 flex items-center gap-2 text-[11px] text-muted">
      <MessageAvatar actor={actor} />
      <span>{actor.name}</span>
    </div>
  )
}
